"use client";

import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Trophy, Calendar, Award, Loader2, Eye, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { adminChallengeAPI } from '../lib/api';
import { toast } from 'sonner';

interface Challenge {
  id: string;
  title: string;
  description: string;
  deadline: string;
  rewards?: string | null;
  status: 'ACTIVE' | 'CLOSED' | 'ARCHIVED';
  submissions: number;
  createdAt: string;
}

interface Submission {
  id: string;
  content: string;
  studentId: string;
  submittedAt: string;
  student?: {
    name: string;
    email: string;
  };
}

export function ChallengesSection() {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSubmissionsDialog, setShowSubmissionsDialog] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState<Challenge | null>(null);
  const [selectedChallenge, setSelectedChallenge] = useState<Challenge | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    deadline: '',
    rewards: '',
  });

  // Fetch all challenges
  const fetchChallenges = async () => {
    try {
      setLoading(true);
      const res = await adminChallengeAPI.getChallenges();
      const data = res?.success ? res.data : res || [];

      const formatted = Array.isArray(data) ? data.map((c: any) => ({
        id: c.id,
        title: c.title,
        description: c.description,
        deadline: c.deadline.split('T')[0],
        rewards: c.rewards,
        status: c.status,
        submissions: c.submissions || 0,
        createdAt: c.createdAt,
      })) : [];

      setChallenges(formatted);
    } catch (err: any) {
      toast.error("Failed to load challenges");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChallenges();
  }, []);

  // Fetch submissions for a challenge
  const fetchSubmissions = async (challengeId: string) => {
    try {
      setLoadingSubmissions(true);
      const res = await adminChallengeAPI.getChallengeSubmissions(challengeId);
      setSubmissions(res?.success ? res.data : []);
    } catch (err: any) {
      toast.error("Failed to load submissions");
      console.error(err);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const handleCreate = () => {
    setEditingChallenge(null);
    setFormData({ title: '', description: '', deadline: '', rewards: '' });
    setShowCreateDialog(true);
  };

  const handleEdit = (challenge: Challenge) => {
    setEditingChallenge(challenge);
    setFormData({
      title: challenge.title,
      description: challenge.description,
      deadline: challenge.deadline,
      rewards: challenge.rewards || '',
    });
    setShowCreateDialog(true);
  };

  const handleViewSubmissions = async (challenge: Challenge) => {
    setSelectedChallenge(challenge);
    setShowSubmissionsDialog(true);
    await fetchSubmissions(challenge.id);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this challenge? This action cannot be undone.')) return;

    try {
      await adminChallengeAPI.deleteChallenge(id);
      setChallenges(prev => prev.filter(c => c.id !== id));
      toast.success('Challenge deleted successfully');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete challenge');
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description || !formData.deadline) {
      toast.error('Title, description, and deadline are required');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        deadline: formData.deadline,
        rewards: formData.rewards.trim() || undefined,
      };

      if (editingChallenge) {
        await adminChallengeAPI.updateChallenge(editingChallenge.id, payload);
        toast.success('Challenge updated successfully!');
      } else {
        await adminChallengeAPI.createChallenge(payload);
        toast.success('New challenge created successfully!');
      }

      fetchChallenges();
      setShowCreateDialog(false);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save challenge');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE': return <Badge className="bg-green-500 text-white">Active</Badge>;
      case 'CLOSED': return <Badge className="bg-red-500 text-white">Closed</Badge>;
      case 'ARCHIVED': return <Badge variant="secondary">Archived</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-12 w-12 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
            Manage Challenges
          </h2>
          <p className="text-gray-600 mt-2">Create and manage monthly innovation challenges</p>
        </div>
        <Button 
          onClick={handleCreate} 
          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-lg px-8 py-6 rounded-2xl"
        >
          <Plus className="w-5 h-5 mr-2" /> Create New Challenge
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-500">Total Challenges</p>
                <p className="text-4xl font-bold">{challenges.length}</p>
              </div>
              <Trophy className="w-12 h-12 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-500">Active Challenges</p>
                <p className="text-4xl font-bold text-green-600">
                  {challenges.filter(c => c.status === 'ACTIVE').length}
                </p>
              </div>
              <Award className="w-12 h-12 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-500">Total Submissions</p>
                <p className="text-4xl font-bold text-blue-600">
                  {challenges.reduce((sum, c) => sum + c.submissions, 0)}
                </p>
              </div>
              <Users className="w-12 h-12 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Challenges Grid */}
      {challenges.length === 0 ? (
        <Card className="p-20 text-center">
          <Trophy className="w-20 h-20 mx-auto text-purple-200 mb-6" />
          <h3 className="text-3xl font-semibold">No Challenges Yet</h3>
          <p className="text-gray-600 mt-3 mb-6">Create your first challenge to inspire students</p>
          <Button onClick={handleCreate} size="lg">Create First Challenge</Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {challenges.map((challenge) => (
            <Card key={challenge.id} className="overflow-hidden border border-purple-100 hover:border-purple-300 transition-all">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-2xl pr-6">{challenge.title}</CardTitle>
                  {getStatusBadge(challenge.status)}
                </div>
              </CardHeader>

              <CardContent className="pt-6 space-y-6">
                <p className="text-gray-700 line-clamp-4 leading-relaxed">
                  {challenge.description}
                </p>

                <div className="flex flex-wrap gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-purple-500" />
                    <div>
                      <p className="text-gray-500">Deadline</p>
                      <p className="font-medium">{new Date(challenge.deadline).toLocaleDateString()}</p>
                    </div>
                  </div>

                  {challenge.rewards && (
                    <div className="flex items-center gap-2">
                      <Award className="w-5 h-5 text-amber-500" />
                      <div>
                        <p className="text-gray-500">Rewards</p>
                        <p className="font-medium">{challenge.rewards}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 bg-purple-50 px-4 py-3 rounded-xl">
                  <Users className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-purple-700">
                    {challenge.submissions} Student Submissions
                  </span>
                </div>
              </CardContent>

              <CardFooter className="gap-3 pt-6">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => handleEdit(challenge)}
                >
                  <Edit className="w-4 h-4 mr-2" /> Edit
                </Button>

                <Button 
                  variant="outline" 
                  className="flex-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                  onClick={() => handleViewSubmissions(challenge)}
                >
                  <Eye className="w-4 h-4 mr-2" /> View Submissions
                </Button>

                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={() => handleDelete(challenge.id)}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit Challenge Dialog - Big & Clean */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {editingChallenge ? 'Edit Challenge' : 'Create New Innovation Challenge'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-6">
            <div>
              <Label className="text-base">Challenge Title</Label>
              <Input
                className="mt-2 text-lg"
                placeholder="E.g. Build a Smart Task Management App"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            <div>
              <Label className="text-base">Full Description</Label>
              <Textarea
                className="mt-2 min-h-[180px] text-base"
                placeholder="Describe the challenge in detail. What should students build? What features are expected?"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Submission Deadline</Label>
                <Input
                  type="date"
                  className="mt-2"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                />
              </div>
              <div>
                <Label>Rewards (Optional)</Label>
                <Input
                  className="mt-2"
                  placeholder="1st Prize: Certificate + Mentorship"
                  value={formData.rewards}
                  onChange={(e) => setFormData({ ...formData, rewards: e.target.value })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={submitting} className="bg-purple-600 hover:bg-purple-700">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingChallenge ? 'Update Challenge' : 'Create Challenge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submissions Modal */}
      <Dialog open={showSubmissionsDialog} onOpenChange={setShowSubmissionsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              Submissions for: <span className="text-purple-700">{selectedChallenge?.title}</span>
            </DialogTitle>
          </DialogHeader>

          {loadingSubmissions ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-10 w-10 animate-spin text-purple-600" />
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              No submissions yet for this challenge.
            </div>
          ) : (
            <div className="space-y-6 max-h-[65vh] overflow-y-auto pr-2">
              {submissions.map((sub, index) => (
                <Card key={sub.id} className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="font-semibold">Student {sub.studentId.slice(0, 8)}...</p>
                      <p className="text-sm text-gray-500">
                        Submitted on {new Date(sub.submittedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge variant="outline">#{index + 1}</Badge>
                  </div>

                  <div className="bg-zinc-950 text-emerald-400 p-5 rounded-xl font-mono text-sm whitespace-pre-wrap overflow-auto max-h-96">
                    {sub.content}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}